import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClinicWidgetSettingResponse } from 'shared/types';
import { Box, Flex, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Logo } from '@televet/kibble-ui/build/components/Logo';
import { Picture } from '@televet/kibble-ui/build/components/Picture';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { GATrack } from 'shared/hooks/useGA';
import { GA4Events } from 'shared/enums/GA4Events';
import { DevClinicIds, ProdClinicIds } from 'shared/enums/ClinicMap';
import { useWidgetStore } from 'state/useWidgetStore';
import WidgetBody from '../shared/WidgetBody';
import { semanticColorTokens } from '@televet/kibble-ui/build/theme/tokens';
import { RequestStep } from 'state/types/steps';

interface WidgetProps {
  widgetSetting: ClinicWidgetSettingResponse;
  onResizeIFrame: () => void;
}

const WidgetExpanded = ({ widgetSetting, onResizeIFrame }: WidgetProps): JSX.Element | null => {
  const {
    selectedRequest,
    isInline,
    isMobile,
    isOpen,
    onToggle,
    requestSteps,
    currentStepIndex,
    clinicId,
    goToPreviousStep,
  } = useWidgetStore((state) => state);
  const { clinicWidgetRequestTypes, clinic } = widgetSetting;

  const isConfirmationStep = useMemo(() => {
    const confirmationSteps: RequestStep[] = ['BookingConfirmation', 'Confirmation', 'RefillConfirmation'];
    return confirmationSteps.includes(requestSteps[currentStepIndex]);
  }, [currentStepIndex, requestSteps]);

  const handleBrandingClick = useCallback(() => {
    Object.assign(document.createElement('a'), {
      target: '_blank',
      href: `https://otto.vet/product/connect/?utm_campaign=Referrals&utm_source=connectwidget&utm_content=${clinicId}`,
    }).click();
  }, [clinicId]);

  const [isDisplayed, setIsDisplayed] = useState(false);
  const [showContainer, setShowContainer] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (isOpen) {
      setShowContainer(true);
    } else {
      setIsDisplayed(false);
      // eslint-disable-next-line
      // @ts-ignore
      timeout = setTimeout(() => {
        setShowContainer(false);
      }, 400);
    }

    return (): void => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen]);

  useEffect(() => {
    onResizeIFrame();

    if (showContainer) {
      setIsDisplayed(true);
    }
  }, [showContainer]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO: We are doing this in too many places, we should move it to a utility function
  // Caring Pathways is a multisite clinic with some specific overrides that will be replaced with settings in future work
  const isCaringPathways = useMemo(
    () =>
      ([
        ProdClinicIds.CaringPathwaysCentral,
        ProdClinicIds.CaringPathwaysEastern,
        ProdClinicIds.CaringPathwaysMountain,
        ProdClinicIds.CaringPathwaysPacific,
        DevClinicIds.ABCDemo,
      ] as string[]).includes(clinicId),
    [clinicId],
  );

  if (!showContainer) return null;

  return (
    <Flex
      className="Widget__Container"
      transform={isDisplayed || isInline ? 'scale(1)' : 'scale(0.3)'}
      transformOrigin="bottom right"
      opacity={isDisplayed ? 1 : 0}
      transition="all 0.3s ease-in-out"
      w={isMobile || isInline ? '100%' : '400px'}
      maxW={isMobile || isInline ? '100%' : 'unset'}
      h={isMobile || isInline ? '100%' : '620px'}
      maxH={(isMobile || isInline) && isDisplayed ? '100vh' : '100%'}
      borderRadius={isMobile || isInline ? 'none' : 'lg'}
      overflow="hidden"
      mb={isMobile || isInline ? 0 : '88px'}
      flexDir="column"
      align="stretch"
      bgColor="background.default"
      boxShadow="base"
    >
      <HStack
        className="Widget__Header"
        bgColor="background.subtle"
        borderBottomWidth={1}
        justify="flex-start"
        align="center"
        p={4}
        spacing={1}
      >
        {selectedRequest && !isConfirmationStep && (
          <Button
            variant="ghostNeutral"
            onClick={goToPreviousStep}
            data-testid="widget-back-button"
            iconName="chevronLeft"
            size="md"
          />
        )}

        {clinic[0].logo && (
          <Box className="Widget__Logo" boxSize="42px" mr={3}>
            <Picture
              src={clinic[0].logo}
              webpSrc={clinic[0].optimizedLogoUrl || ''}
              objectFit="cover"
              w="100%"
              h="100%"
              imgProps={{
                alt: `${clinic[0].name} logo`,
                loading: 'lazy',
                borderRadius: 'base',
              }}
            />
          </Box>
        )}

        <VStack className="Widget__SubHeader" align="flex-start" justify="center" spacing={0}>
          <Heading size="sm">{widgetSetting.clinic[0].name}</Heading>
          <Text size="sm">
            {isCaringPathways ? (
              'We are here to help.'
            ) : (
              <>
                Welcome!{' '}
                <span role="img" aria-label="Wave">
                  👋
                </span>{' '}
                What can we help you with?
              </>
            )}
          </Text>
        </VStack>

        {isMobile && <Button onClick={(): void => onToggle(false)} variant="ghostNeutral" iconName="close" size="lg" />}
      </HStack>

      <WidgetBody requestTypes={clinicWidgetRequestTypes} widgetSetting={widgetSetting} />

      <Flex
        className="Widget__Footer"
        align="center"
        justify="center"
        p={2}
        cursor="pointer"
        borderTopWidth={1}
        role="group"
        // Override theme colors, Powered by Otto and Otto logo should always be Otto colors
        sx={{
          'svg *': { fill: semanticColorTokens['text.interactive'].default },
          _hover: { 'svg *': { fill: semanticColorTokens['text.interactive.hover'].default } },
        }}
        onClick={(): void => {
          GATrack(GA4Events.OTTO_CLICK, {
            clinicId: clinic[0].id,
            requestType: selectedRequest?.requestType,
          });
          handleBrandingClick();
        }}
      >
        <Logo type="icon" width={4} mr={2} />
        <Text
          size="sm"
          color={semanticColorTokens['text.interactive'].default}
          _groupHover={{ color: semanticColorTokens['text.interactive.hover'].default }}
        >
          Powered by Otto&reg;
        </Text>
      </Flex>
    </Flex>
  );
};

export default WidgetExpanded;
